import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/i18nContext';
import { useTheme } from '../context/ThemeContext';
import { isAgent, isAdmin } from '../constants/roles';
import { statusKey, statusChip, AGENT_SETTABLE } from '../constants/caseStatus';
import { PRIORITIES, priorityKey, priorityChip } from '../constants/priority';
import CaseJobsSection, { JOB_TERMINAL } from '../components/CaseJobsSection';
import AppHeader from '../components/AppHeader';
import ProfileDrawer from '../components/ProfileDrawer';
import Button from '../components/ui/Button';
import '../styles/CaseDetail.css';

// Auth-os kép betöltés (a /file endpoint Bearer tokent kér → blob + object URL)
const AuthImage = ({ api, url, alt, className, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false; let objUrl;
    api.get(url, { responseType: 'blob' })
      .then((r) => { if (!cancelled) { objUrl = URL.createObjectURL(r.data); setSrc(objUrl); } })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  return src
    ? <img src={src} alt={alt} className={className} onClick={onClick} />
    : <div className={`${className} cs-img-loading`} />;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

const CaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user, logout } = useAuth();
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const staff = isAgent(user?.role);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [body, setBody]       = useState('');
  const [internal, setInternal] = useState(false);
  const [files, setFiles]     = useState([]);
  const [busy, setBusy]       = useState(false);
  const [agents, setAgents]   = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [activeJobs, setActiveJobs] = useState(0);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const onJobsLoaded = useCallback((jobs) => {
    setActiveJobs(jobs.filter(j => !JOB_TERMINAL.includes(j.status)).length);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/cases/${id}`);
      setData(d);
    } catch (e) {
      setError(e.response?.status === 403 ? t('cases.forbidden') : t('cases.notFound'));
    } finally {
      setLoading(false);
    }
  }, [api, id, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAdmin(user?.role)) api.get('/cases/agents').then(r => setAgents(r.data)).catch(() => {});
  }, [api, user]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setBusy(false); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!body.trim() && files.length === 0) return;
    setBusy(true);
    try {
      let msgId = null;
      if (body.trim()) {
        const { data: msg } = await api.post(`/cases/${id}/messages`, {
          body: body.trim(), language, is_internal: internal,
        });
        msgId = msg.id;
      }
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        if (msgId) fd.append('message_id', msgId);
        await api.post(`/cases/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setBody(''); setFiles([]); setInternal(false);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.error || 'Error');
    } finally { setBusy(false); }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  if (loading) return <div className={`cs-page ${isDark ? 'theme-dark' : 'theme-light'}`}><div className="cs-loading">{t('common.loading')}</div></div>;
  if (error && !data) return (
    <div className={`cs-page ${isDark ? 'theme-dark' : 'theme-light'}`}>
      <div className="cs-loading">{error}</div>
      <Button variant="ghost" onClick={handleBack}>← {t('cases.back')}</Button>
    </div>
  );

  const c = data;
  const caseAttachments = c.attachments.filter(a => !a.message_id);
  const tr = (txt, translations) => {
    // staff: a célnyelvi fordítás(ok); customer: a saját nyelvére fordítva
    if (!translations) return null;
    const langs = Object.keys(translations);
    return langs.length ? translations : null;
  };

  const handleLogout = async () => {
    await logout();
  };

  // Ügyszám + cím a fejléc kontextus-sávjába (a logo+brand helyett)
  const caseTitle = (
    <>
      <span className="cs-case-number">{c.case_number}</span>
      <span className="cs-header-title">
        {c.title}
        {c.language && <span className="cs-lang-badge">{c.language.toUpperCase()}</span>}
      </span>
    </>
  );

  // Meta a fejléc második, olvasható sorába
  const caseMeta = (
    <>
      {c.property_id && (
        <span className="cs-meta-property">
          🏠{' '}
          {staff ? (
            <Link to={`/admin/properties?edit=${c.property_id}`} className="cs-property-link">
              {c.property_name}{c.property_city ? ` — ${c.property_city}` : ''}
            </Link>
          ) : (
            <span>{c.property_name}{c.property_city ? ` — ${c.property_city}` : ''}</span>
          )}
        </span>
      )}
      <span>{t('cases.opened')}: {fmtDate(c.created_at)}</span>
      {staff && <span>{t('cases.customer')}: {c.customer_username || c.customer_email}</span>}
      <span>{t('cases.agent')}: {c.agent_username || '—'}</span>
    </>
  );

  return (
    <div className={`cs-page ${isDark ? 'theme-dark' : 'theme-light'}`}>
      {/* SHARED APP HEADER — ügy-kontextussal (nincs külön második fejléc) */}
      <AppHeader
        user={user}
        onLogout={handleLogout}
        onOpenProfile={() => setProfileDrawerOpen(true)}
        onBack={handleBack}
        title={caseTitle}
        subBar={caseMeta}
      />

      <div className="cs-body">
        {/* FŐ OSZLOP */}
        <main className="cs-main">
          {c.titleTranslations && Object.entries(c.titleTranslations).map(([lng, txt]) => (
            <div key={lng} className="cs-title-translation">
              <span className="cs-tr-lang">{lng.toUpperCase()}</span> {txt}
            </div>
          ))}

          {/* LEÍRÁS */}
          {c.description && (
            <div className="cs-description">
              <p>{c.description}</p>
              {tr(c.description, c.descriptionTranslations) && (
                <div className="cs-translations">
                  {Object.entries(c.descriptionTranslations).map(([lng, txt]) => (
                    <div key={lng} className="cs-translation">
                      <span className="cs-tr-lang">{lng.toUpperCase()}</span> {txt}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AUTO-CLOSE TÁJÉKOZTATÓ */}
          {c.status === 'resolved' && (
            <div className="cs-autoclose-notice">
              ⏳ This case has been marked as resolved{c.agent_confirmed_at ? ` on ${fmtDate(c.agent_confirmed_at)}` : ''}. It will be automatically closed after 72 hours inactivity.
            </div>
          )}

          {/* CASE-SZINTŰ CSATOLMÁNYOK */}
          {caseAttachments.length > 0 && (
            <div className="cs-attach-grid">
              {caseAttachments.map(a => (
                <AttachmentThumb key={a.id} api={api} caseId={id} att={a} onOpen={setLightbox} t={t} />
              ))}
            </div>
          )}

          {/* SZOLGÁLTATÓI FOLYAMATOK — csak staff */}
          {staff && (
            <CaseJobsSection api={api} caseId={id} t={t} language={language}
              isDark={isDark} onJobsLoaded={onJobsLoaded} />
          )}

          {/* IDŐVONAL */}
          <div className="cs-timeline">
            {c.messages.map(m => {
              const mine = staff
                ? (m.author_role !== null && m.author_role !== 2)
                : m.author_id === user?.id;
              const msgAtts = c.attachments.filter(a => a.message_id === m.id);
              const translations = tr(m.body, m.translations);
              return (
                <div key={m.id} className={`cs-msg ${mine ? 'cs-msg-mine' : ''} ${m.is_internal ? 'cs-msg-internal' : ''}`}>
                  <div className="cs-msg-head">
                    <span className="cs-msg-author">{m.author_username || '—'}</span>
                    {m.is_internal === 1 && <span className="cs-internal-badge">{t('cases.internalNote')}</span>}
                    <span className="cs-msg-time">{fmtDate(m.created_at)}</span>
                  </div>
                  <div className="cs-msg-body">{m.body}</div>
                  {translations && (
                    <div className="cs-translations">
                      {Object.entries(translations).map(([lng, txt]) => (
                        <div key={lng} className="cs-translation">
                          <span className="cs-tr-lang">{lng.toUpperCase()}</span> {txt}
                        </div>
                      ))}
                    </div>
                  )}
                  {msgAtts.length > 0 && (
                    <div className="cs-attach-grid">
                      {msgAtts.map(a => (
                        <AttachmentThumb key={a.id} api={api} caseId={id} att={a} onOpen={setLightbox} t={t} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {c.messages.length === 0 && <p className="cs-empty">{t('cases.noMessages')}</p>}
          </div>

          {/* ÜZENETÍRÓ */}
          {c.status !== 'closed' && (
            <form className="cs-composer" onSubmit={sendMessage}>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
                placeholder={t('cases.writeMessage')} />
              <div className="cs-composer-row">
                <input type="file" multiple accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setFiles(Array.from(e.target.files))} />
                {staff && (
                  <label className="cs-internal-toggle">
                    <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                    {t('cases.internalNote')}
                  </label>
                )}
                <button type="submit" className="cs-btn-primary" disabled={busy}>{t('cases.send')}</button>
              </div>
            </form>
          )}
        </main>

        {/* OLDALSÁV */}
        <aside className="cs-sidebar">

          {/* STÁTUSZ KÁRTYA */}
          <div className="cs-status-card">
            <div className="cs-status-row">
              <span className="cs-status-label-sm">{t('cases.status') || 'Státusz'}</span>
              <span className={statusChip(c.status)}>{t(statusKey(c.status))}</span>
            </div>
            <div className="cs-status-row">
              <span className="cs-status-label-sm">{t('priority.label')}</span>
              <span className={priorityChip(c.priority)}>{t(priorityKey(c.priority || 'normal'))}</span>
            </div>
            <div className="cs-status-row">
              <span className="cs-status-label-sm">{t('cases.agent')}</span>
              <span className="cs-status-value">{c.agent_username || '—'}</span>
            </div>
            <div className="cs-status-row">
              <span className="cs-status-label-sm">{t('cases.dueDate')}</span>
              <span className="cs-status-value">{c.due_date ? new Date(c.due_date).toLocaleDateString() : '—'}</span>
            </div>
          </div>

          {/* ACTIONS KÁRTYA */}
          <div className="cs-actions">
            <h3 className="cs-actions-title">{t('cases.actions')}</h3>

          {/* AGENT műveletek */}
          {staff && (
            <>
              {!c.assigned_agent_id && (
                <button className="cs-btn-primary" disabled={busy}
                  onClick={() => act(() => api.post(`/cases/${id}/claim`))}>
                  {t('cases.claim')}
                </button>
              )}
              {isAdmin(user?.role) && (
                <label className="cs-field">
                  <span>{t('cases.assignAgent')}</span>
                  <select value={c.assigned_agent_id || ''} disabled={busy}
                    onChange={(e) => act(() => api.post(`/cases/${id}/assign`, { agent_id: e.target.value || null }))}>
                    <option value="">—</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                  </select>
                </label>
              )}
              <label className="cs-field">
                <span>{t('priority.label')}</span>
                <select value={c.priority || 'normal'} disabled={busy}
                  onChange={(e) => act(() => api.post(`/cases/${id}/priority`, { priority: e.target.value }))}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{t(priorityKey(p))}</option>)}
                </select>
              </label>
              <label className="cs-field">
                <span>{t('cases.dueDate')}</span>
                <input type="date" disabled={busy}
                  value={c.due_date ? c.due_date.slice(0, 10) : ''}
                  onChange={(e) => act(() => api.post(`/cases/${id}/due-date`, { due_date: e.target.value || null }))}
                />
              </label>
              {!['resolved', 'closed'].includes(c.status) && (
                <details className="cs-override">
                  <summary>{t('cases.overrideStatus')}</summary>
                  <select value="" disabled={busy}
                    onChange={(e) => e.target.value && act(() => api.post(`/cases/${id}/status`, { status: e.target.value }))}>
                    <option value="">—</option>
                    {AGENT_SETTABLE.map(s => <option key={s} value={s}>{t(statusKey(s))}</option>)}
                  </select>
                </details>
              )}
              {!['resolved', 'closed'].includes(c.status) && (
                <>
                  <div className="cs-btn-spacer" />
                  <div className="cs-btn-spacer" />
                  <button className="cs-btn-primary" disabled={busy}
                    onClick={() => setShowResolveConfirm(true)}>
                    {t('cases.markResolved')}
                  </button>
                </>
              )}
              {isAdmin(user?.role) && (
                <button className="cs-btn-danger" disabled={busy}
                  onClick={async () => {
                    if (!window.confirm(t('cases.deleteConfirm'))) return;
                    setBusy(true);
                    try { await api.delete(`/cases/${id}`); navigate('/dashboard'); }
                    catch (e2) { setError(e2.response?.data?.error || 'Error'); setBusy(false); }
                  }}>
                  {t('cases.deleteCase')}
                </button>
              )}
            </>
          )}

          {/* CUSTOMER műveletek */}
          {!staff && c.status === 'resolved' && (
            <button className="cs-btn-primary" disabled={busy}
              onClick={() => act(() => api.post(`/cases/${id}/confirm`))}>
              {t('cases.confirmClose')}
            </button>
          )}

          {/* MINDKÉT FÉL: újranyitás */}
          {['resolved', 'closed'].includes(c.status) && (
            <button className="cs-btn-ghost" disabled={busy}
              onClick={() => act(() => api.post(`/cases/${id}/reopen`))}>
              {t('cases.reopen')}
            </button>
          )}

          {error && <div className="cs-modal-error">{error}</div>}
          </div>{/* /cs-actions */}
        </aside>{/* /cs-sidebar */}
      </div>

      {/* RESOLVE CONFIRM MODAL */}
      {showResolveConfirm && (
        <div className="cs-modal-overlay" onClick={() => setShowResolveConfirm(false)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()}>
            <h2 className="cs-modal-title">{t('cases.markResolved')}</h2>
            <p className="cs-resolve-modal-text">
              {t('cases.resolveConfirmText') || 'Biztosan megoldottnak jelöli az ügyet? Az ügyfél visszajelzése hiányában az ügy 72 óra múlva automatikusan lezáródik.'}
            </p>
            {activeJobs > 0 && (
              <p className="cs-resolve-modal-text jb-warning">
                ⚠️ {t('jobs.openJobsWarning').replace('{n}', activeJobs)}
              </p>
            )}
            <div className="cs-modal-actions">
              <button className="cs-btn-ghost" onClick={() => setShowResolveConfirm(false)}>
                {t('common.no') || 'Nem'}
              </button>
              <button className="cs-btn-primary" disabled={busy} onClick={() => {
                setShowResolveConfirm(false);
                act(() => api.post(`/cases/${id}/resolve`));
              }}>
                {t('common.yes') || 'Igen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="cs-lightbox" onClick={() => setLightbox(null)}>
          <AuthImage api={api} url={`/cases/${id}/attachments/${lightbox}/file?variant=converted`} alt="" className="cs-lightbox-img" />
        </div>
      )}

      {/* PROFILE DRAWER */}
      {profileDrawerOpen && user && (
        <ProfileDrawer
          userId={user.id}
          onClose={() => setProfileDrawerOpen(false)}
        />
      )}
    </div>
  );
};

// Csatolmány-thumbnail (kép → thumb; egyéb → letöltő link)
const AttachmentThumb = ({ api, caseId, att, onOpen, t }) => {
  if (att.kind === 'image' || att.has_thumb) {
    return (
      <div className="cs-attach">
        <AuthImage api={api} url={`/cases/${caseId}/attachments/${att.id}/file?variant=thumb`}
          alt="" className="cs-attach-thumb" onClick={() => onOpen(att.id)} />
        {att.kind === 'proof' && <span className="cs-proof-badge">{t('cases.proof')}</span>}
      </div>
    );
  }
  const downloadFile = async () => {
    const r = await api.get(`/cases/${caseId}/attachments/${att.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = `attachment-${att.id}`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <button type="button" className="cs-attach-file" onClick={downloadFile}>
      {att.kind === 'video' ? '🎬' : '📎'} {t('cases.download')}
    </button>
  );
};

export default CaseDetailPage;
